import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Progress,
  Button,
  Space,
  Typography,
  List,
  Tag,
  Tooltip,
  Badge,
  Divider,
  Modal,
  message,
} from "antd";
import {
  MinusOutlined,
  ExpandOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  DragOutlined,
  WarningOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import { useUpload } from "contexts/UploadContext";
import { UPLOAD_STATUS } from "constants/S3Constant";
import { useBackgroundUpload } from "utils/hooks/useBackgroundUpload";
import { useRouteDetection } from "utils/hooks/useRouteDetection";
import { APP_PREFIX_PATH } from "configs/AppConfig";

const { Text, Title } = Typography;
const { confirm } = Modal;

// Add CSS for animated success tick
const successAnimationStyles = `
  @keyframes checkmark {
    0% {
      transform: scale(0) rotate(45deg);
      opacity: 0;
    }
    50% {
      transform: scale(1.2) rotate(45deg);
      opacity: 1;
    }
    100% {
      transform: scale(1) rotate(45deg);
      opacity: 1;
    }
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(82, 196, 26, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(82, 196, 26, 0);
    }
  }
  
  .animated-success-tick {
    animation: checkmark 0.6s ease-in-out, pulse 2s infinite;
    color: #52c41a !important;
    font-size: 18px;
  }
  
  .success-message-container {
    background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
    
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 12px;
    animation: slideIn 0.5s ease-out;
  }
  
  @keyframes slideIn {
    from {
      transform: translateY(-10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const FloatingUploadWidget = () => {
  // All hooks must be called before any early returns
  const { shouldHideWidget } = useRouteDetection();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    uploads,
    isUploading,
    totalFiles,
    completedFiles,
    failedFiles,
    cancelledFiles,
    isMinimized,
    isCancelled,
    position,
    toggleMinimize,
    updatePosition,
    clearUploads,
    cancelUpload,
  } = useUpload();

  const { retryFailedUploads } = useBackgroundUpload();
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const widgetRef = useRef(null);

  // Calculate display values
  const overallProgress =
    totalFiles > 0
      ? Math.round(
          ((completedFiles + failedFiles + cancelledFiles) / totalFiles) * 100
        )
      : 0;
  const isComplete =
    !isUploading &&
    completedFiles + failedFiles + cancelledFiles === totalFiles;

  // Move all event handlers and effects before early returns
  const handleRetry = () => {
    retryFailedUploads();
  };

  const handleCancel = () => {
    const activeUploads = uploads.filter(
      (upload) =>
        upload.status === UPLOAD_STATUS.UPLOADING ||
        upload.status === UPLOAD_STATUS.PENDING
    );

    if (activeUploads.length === 0) {
      message.info("No active uploads to cancel");
      return;
    }

    confirm({
      title: "Cancel Upload",
      icon: <WarningOutlined />,
      content: (
        <div>
          <p>Are you sure you want to cancel the upload?</p>
          <p>
            This will stop all pending and ongoing uploads (
            {activeUploads.length} files).
          </p>
        </div>
      ),
      okText: "Yes, Cancel Upload",
      okType: "danger",
      cancelText: "No, Continue",
      onOk() {
        cancelUpload();
        message.success("Upload cancelled successfully");
      },
      onCancel() {
        console.log("Cancel confirmation dismissed");
      },
    });
  };

  const handleClearUploads = () => {
    if (isUploading) {
      confirm({
        title: "Clear Upload History",
        icon: <WarningOutlined />,
        content: (
          <div>
            <p>There are still uploads in progress.</p>
            <p>
              Clearing will cancel all uploads and remove the history. Are you
              sure?
            </p>
          </div>
        ),
        okText: "Yes, Clear All",
        okType: "danger",
        cancelText: "No, Keep",
        onOk() {
          clearUploads();
          setShowSuccessMessage(false);
          message.success("Upload history cleared");
        },
      });
    } else {
      clearUploads();
      setShowSuccessMessage(false);
      message.success("Upload history cleared");
    }
  };

  const handleGoToImportPage = () => {
    navigate(`${APP_PREFIX_PATH}/school-management/student/import-pose`);
  };

  const handleMouseDown = (e) => {
    if (e.target.closest(".drag-handle")) {
      setIsDragging(true);
      const rect = widgetRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const maxX = window.innerWidth - 400;
      const maxY = window.innerHeight - 100;

      updatePosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle minimize/expand - completed uploads should stay minimized
  const handleToggleMinimize = () => {
    if (!isComplete) {
      toggleMinimize();
    }
  };

  // useEffect must be called before any early returns
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Add styles to head
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = successAnimationStyles;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Early returns should come AFTER all hooks
  // Don't render if no uploads or should hide widget
  if (totalFiles === 0 || shouldHideWidget) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case UPLOAD_STATUS.SUCCESS:
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case UPLOAD_STATUS.FAILED:
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
      case UPLOAD_STATUS.UPLOADING:
        return <LoadingOutlined style={{ color: "#1890ff" }} />;
      case UPLOAD_STATUS.CANCELLED:
        return <StopOutlined style={{ color: "#8c8c8c" }} />;
      default:
        return <PauseCircleOutlined style={{ color: "#8c8c8c" }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case UPLOAD_STATUS.SUCCESS:
        return "success";
      case UPLOAD_STATUS.FAILED:
        return "error";
      case UPLOAD_STATUS.UPLOADING:
        return "processing";
      case UPLOAD_STATUS.CANCELLED:
        return "warning";
      default:
        return "default";
    }
  };

  const failedUploads = uploads.filter(
    (upload) => upload.status === UPLOAD_STATUS.FAILED
  );
  const activeUploads = uploads.filter(
    (upload) => upload.status === UPLOAD_STATUS.UPLOADING
  );

  return (
    <div
      ref={widgetRef}
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 1000,
        userSelect: "none",
        cursor: isDragging ? "grabbing" : "default",
        pointerEvents: "auto",
      }}
      onMouseDown={handleMouseDown}
    >
      <Card
        style={{
          width: 400,
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          borderRadius: 8,
          overflow: "hidden",
          border:0
            // isComplete && completedFiles > 0
            //   ? "1px solid #e8e8e8"
            //   : "1px solid #e8e8e8",
        }}
        bodyStyle={{
          padding: isMinimized || isComplete ? "12px 16px" : "16px",
          transition: "all 0.3s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: isMinimized || isComplete ? 0 : 16,
          }}
        >
          <Space>
            <div
              className="drag-handle"
              style={{ cursor: "grab", padding: "4px" }}
            >
              <DragOutlined />
            </div>
            {isComplete && completedFiles > 0 ? (
              <CheckCircleOutlined className="animated-success-tick" />
            ) : null}
            <Title level={5} style={{ margin: 0, fontSize: "14px" }}>
              {isUploading
                ? "Uploading..."
                : isCancelled
                ? "Upload Cancelled"
                : isComplete
                ? `${completedFiles} Images Uploaded Successfully!`
                : "Upload Status"}
            </Title>
            <Badge
              count={isUploading ? activeUploads.length : 0}
              size="small"
              style={{ backgroundColor: "#1890ff" }}
            />
          </Space>
          <Space>
            {isComplete && (
              <Tooltip title="Go to Import Page">
                <Button
                  type="default"
                  icon={<FolderOpenOutlined />}
                  onClick={handleGoToImportPage}
                  size="small"
                  style={{ fontSize: "12px" }}
                >
                  Import Page
                </Button>
              </Tooltip>
            )}
            {!isComplete && (
              <Tooltip title="Go to Import Page">
                <Button
                  type="text"
                  icon={<FolderOpenOutlined />}
                  onClick={handleGoToImportPage}
                  size="small"
                  style={{ fontSize: "12px" }}
                />
              </Tooltip>
            )}
            {!isComplete && (
              <Tooltip title={isMinimized ? "Expand" : "Minimize"}>
                <Button
                  type="text"
                  icon={isMinimized ? <ExpandOutlined /> : <MinusOutlined />}
                  onClick={handleToggleMinimize}
                  size="small"
                  style={{ fontSize: "12px" }}
                />
              </Tooltip>
            )}
            <Tooltip title="Clear History">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClearUploads}
                size="small"
                style={{ fontSize: "12px" }}
              />
            </Tooltip>
          </Space>
        </div>

        {!isMinimized && !isComplete && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Progress
                percent={overallProgress}
                status={
                  isUploading
                    ? "active"
                    : failedFiles > 0
                    ? "exception"
                    : isCancelled
                    ? "normal"
                    : "success"
                }
                format={() => `${overallProgress}%`}
                strokeWidth={6}
                trailColor="#f0f0f0"
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {completedFiles + failedFiles + cancelledFiles} of{" "}
                  {totalFiles} files
                </Text>
                <Space>
                  <Text type="success" style={{ fontSize: "12px" }}>
                    {completedFiles} completed
                  </Text>
                  {failedFiles > 0 && (
                    <Text type="danger" style={{ fontSize: "12px" }}>
                      {failedFiles} failed
                    </Text>
                  )}
                  {cancelledFiles > 0 && (
                    <Text type="warning" style={{ fontSize: "12px" }}>
                      {cancelledFiles} cancelled
                    </Text>
                  )}
                </Space>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button
                  icon={<FolderOpenOutlined />}
                  onClick={handleGoToImportPage}
                  size="small"
                  style={{ fontSize: "12px" }}
                >
                  Go to Import Page
                </Button>
                {isUploading && (
                  <Button
                    icon={<StopOutlined />}
                    onClick={handleCancel}
                    size="small"
                    danger
                    style={{ fontSize: "12px" }}
                  >
                    Cancel Upload
                  </Button>
                )}
                {failedUploads.length > 0 && (
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRetry}
                    size="small"
                    type="primary"
                    style={{ fontSize: "12px" }}
                  >
                    Retry Failed ({failedUploads.length})
                  </Button>
                )}
              </Space>
            </div>

            <Divider style={{ margin: "8px 0" }} />

            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              <List
                size="small"
                dataSource={uploads.slice(0, 20)}
                renderItem={(upload) => (
                  <List.Item
                    style={{
                      padding: "6px 0",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 4,
                        }}
                      >
                        <Space>
                          {getStatusIcon(upload.status)}
                          <Text strong style={{ fontSize: "12px" }}>
                            {upload.student_id}
                          </Text>
                          <Text type="secondary" style={{ fontSize: "11px" }}>
                            → {upload.file_name}
                          </Text>
                        </Space>
                        <Space>
                          <Tag
                            color={getStatusColor(upload.status)}
                            size="small"
                            style={{ fontSize: "10px" }}
                          >
                            {upload.status}
                          </Tag>
                          {upload.status === UPLOAD_STATUS.UPLOADING && (
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                              {upload.progress}%
                            </Text>
                          )}
                        </Space>
                      </div>

                      {upload.status === UPLOAD_STATUS.UPLOADING && (
                        <Progress
                          percent={upload.progress}
                          size="small"
                          style={{ marginTop: 2 }}
                          strokeWidth={3}
                          trailColor="#f0f0f0"
                        />
                      )}

                      {upload.error && (
                        <Text
                          type="danger"
                          style={{
                            fontSize: "10px",
                            display: "block",
                            marginTop: 2,
                          }}
                        >
                          {upload.error}
                        </Text>
                      )}
                    </div>
                  </List.Item>
                )}
              />

              {uploads.length > 20 && (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <Text type="secondary" style={{ fontSize: "11px" }}>
                    ... and {uploads.length - 20} more files
                  </Text>
                </div>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default FloatingUploadWidget;
